// XHR-based upload helper with progress events. fetch() can't emit upload
// progress on JS bodies in Chrome today (requires HTTP/2 + experimental
// flag), so file uploads that need a progress bar use XHR instead.
//
// Always sends cookies via `withCredentials = true`. Extra headers (e.g.
// X-API-Key when uploading to a localhost service the cookie can't reach)
// can be passed in `headers`.
//
// Two failures are enforced HERE rather than in each caller, because they are
// properties of the transport and every uploader has them: a body the proxy
// will refuse outright, and a body the proxy has stopped reading. See
// uploadLimits.js for what a 434 MB PLY did without them — it hung forever,
// silently, with the modal telling the user to be patient.

import { oversizeRefusal, stallVerdict, STALL_MS } from './uploadLimits.js'

/**
 * The uploaded body's size in bytes, or null when it cannot be measured.
 *
 * Only the `file` field is measured: the rest of a FormData is a few hundred
 * bytes of text fields, and the cap exists for the multi-hundred-MB splat. A
 * FormData implementation without `.get` (or a caller sending something else)
 * yields null, which the refusal rule treats as "not evidence of oversize".
 */
function bodyBytes(formData) {
  if (typeof formData?.get !== 'function') return null
  const f = formData.get('file')
  return typeof f?.size === 'number' ? f.size : null
}

export function postFileWithProgress(url, formData, opts = {}) {
  const { onProgress, headers = {}, method = 'POST', stallMs = STALL_MS } = opts

  // Refused before a byte is sent. This one cannot be reported from the wire:
  // nginx answers 413 and stops reading, and the browser never surfaces that
  // response to XHR over HTTP/2 — so if we don't catch it here, nobody does.
  const refusal = oversizeRefusal(bodyBytes(formData))
  if (refusal) return Promise.reject(new Error(refusal))

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)
    xhr.withCredentials = true
    for (const [k, v] of Object.entries(headers)) {
      if (v !== undefined && v !== null && v !== '') xhr.setRequestHeader(k, v)
    }
    // Stall watchdog. Armed on send, disarmed on any terminal event, and reset
    // by real forward progress — so a slow upload runs as long as it needs and
    // a stopped one fails with a message naming where it stopped.
    let lastProgressAt = null
    let lastLoaded = -1
    // The multipart body is LARGER than the file: boundaries and the text
    // fields ride along. So the browser's own `e.total` is what `loaded` is
    // counting towards, and bodyBytes (the file alone) is only the fallback for
    // a transport that reports no length. Comparing loaded against the smaller
    // number would call the send complete a few hundred bytes early.
    let lastTotal = 0
    let timer = null
    const disarm = () => { if (timer !== null) { clearInterval(timer); timer = null } }
    const arm = () => {
      lastProgressAt = Date.now()
      timer = setInterval(() => {
        const verdict = stallVerdict({
          lastProgressAt, now: Date.now(), loaded: Math.max(lastLoaded, 0),
          total: lastTotal || bodyBytes(formData) || 0, stallMs,
        })
        if (!verdict) return
        disarm()
        // Abort first: the request is wedged in the browser's send queue and
        // would otherwise hold its stream (and its copy of the file) for the
        // life of the tab. `abort` fires no `error`, so reject explicitly.
        xhr.abort()
        reject(new Error(verdict))
      }, 1000)
    }

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && e.total > 0) lastTotal = e.total
      if (e.loaded > lastLoaded) { lastLoaded = e.loaded; lastProgressAt = Date.now() }
      if (typeof onProgress !== 'function') return
      if (e.lengthComputable) {
        onProgress({ loaded: e.loaded, total: e.total, fraction: e.loaded / e.total })
      } else {
        onProgress({ loaded: e.loaded, total: 0, fraction: -1 })
      }
    })
    xhr.addEventListener('loadend', disarm)
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? JSON.parse(xhr.responseText) : null)
        } catch (err) {
          reject(new Error(`Upload OK but bad JSON response: ${err.message}`))
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText || xhr.statusText}`))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('Upload network error')))
    xhr.send(formData)
    arm()
  })
}
